import { ChangeEvent, useCallback } from 'react';

/**
 * Hook pour créer un gestionnaire de mise à jour partielle depuis un événement de changement
 *
 * Ce hook crée une fonction qui :
 * - Extrait la valeur de l'événement de changement
 * - Met à jour seulement la propriété spécifiée de l'objet
 * - Utilise useCallback pour optimiser les performances
 *
 * @template T Type de l'objet à mettre à jour
 * @param key Clé de la propriété à mettre à jour
 * @param updater Fonction de mise à jour de l'objet
 * @returns Fonction de gestionnaire d'événement de changement
 */
export const useChangeEventPartialUpdate = <T>(
  key: keyof T,
  updater: (payload: Partial<T>) => void,
): ((e: ChangeEvent<{ name?: string; value: string }>) => void) => {
  return useCallback(
    (e: ChangeEvent<{ name?: string; value: string }>) =>
      updater({ [key]: e.target.value } as any),
    [key, updater],
  );
};

/**
 * Hook pour créer un gestionnaire de mise à jour complète depuis un événement de changement
 *
 * Ce hook crée une fonction qui :
 * - Extrait la valeur de l'événement de changement
 * - Met à jour l'objet complet avec la nouvelle valeur
 * - Utilise useCallback pour optimiser les performances
 *
 * @template T Type de la valeur à mettre à jour
 * @param updater Fonction de mise à jour
 * @returns Fonction de gestionnaire d'événement de changement
 */
export const useChangeEventUpdate = <T>(
  updater: (payload: T) => void,
): ((e: ChangeEvent<{ name?: string; value: unknown }>) => void) => {
  return useCallback(
    (e: ChangeEvent<{ name?: string; value: unknown }>) =>
      updater(e.target.value as T),
    [updater],
  );
};

/**
 * Hook pour créer un gestionnaire de remplacement d'objet depuis un événement de changement
 *
 * Ce hook crée une fonction qui :
 * - Extrait la valeur de l'événement de changement
 * - Remplace l'objet existant en modifiant seulement la propriété spécifiée
 * - Utilise useCallback pour optimiser les performances
 *
 * @template T Type de l'objet à remplacer
 * @param key Clé de la propriété à modifier
 * @param value Objet existant à modifier
 * @param updater Fonction de mise à jour
 * @returns Fonction de gestionnaire d'événement de changement
 */
export const useChangeEventOverride = <T>(
  key: keyof T,
  value: T,
  updater: (payload: T) => void,
): ((e: ChangeEvent<{ name?: string; value: unknown }>) => void) => {
  return useCallback(
    (e: ChangeEvent<{ name?: string; value: unknown }>) =>
      updater({ ...value, [key]: e.target.value }),
    [key, value, updater],
  );
};

/**
 * Hook pour créer un gestionnaire de mise à jour partielle depuis une valeur
 *
 * Ce hook crée une fonction qui :
 * - Met à jour seulement la propriété spécifiée de l'objet
 * - Utilise useCallback pour optimiser les performances
 * - Permet de passer une valeur partielle pour mise à jour
 *
 * @template T Type de l'objet à mettre à jour
 * @param key Clé de la propriété à mettre à jour
 * @param updater Fonction de mise à jour de l'objet
 * @returns Fonction de mise à jour partielle
 */
export const useValuePartialUpdate = <T>(
  key: keyof T,
  updater: (payload: T) => void,
) => {
  return useCallback((value: Partial<T>) => updater({ [key]: value } as any), [
    key,
    updater,
  ]);
};
